/**
 * Infinite Scroll for Shopify Dawn Collection Pages
 * Automatically loads more products as the user scrolls down.
 * Falls back to regular pagination if JS fails.
 */
(function () {
  'use strict';

  const SCROLL_THRESHOLD = 600; // px from bottom to trigger load
  const DEBOUNCE_MS = 100;

  class InfiniteScroll {
    constructor() {
      this.productGrid = document.getElementById('product-grid');
      this.gridContainer = document.getElementById('ProductGridContainer');
      this.paginationWrapper = null;
      this.loading = false;
      this.noMorePages = false;
      this.nextUrl = null;
      this.sectionId = null;
      this.spinnerEl = null;

      if (!this.productGrid || !this.gridContainer) return;

      this.sectionId = this.productGrid.dataset.id;
      this._findNextPage();
      if (!this.nextUrl) return; // single page — nothing to do

      this._hidePagination();
      this._createSpinner();
      this._bindScroll();
    }

    /** Find the "next page" link from existing pagination */
    _findNextPage() {
      const nextLink = this.gridContainer.querySelector(
        '.pagination__item--prev' // Dawn uses --prev for the "next" arrow (confusing naming)
      );
      if (nextLink) {
        this.nextUrl = nextLink.getAttribute('href');
        this.paginationWrapper = nextLink.closest('.pagination-wrapper');
      }
    }

    /** Hide the numbered pagination — we'll show a spinner instead */
    _hidePagination() {
      if (this.paginationWrapper) {
        this.paginationWrapper.style.display = 'none';
      }
    }

    /** Create a loading spinner element */
    _createSpinner() {
      this.spinnerEl = document.createElement('div');
      this.spinnerEl.className = 'infinite-scroll-spinner';
      this.spinnerEl.innerHTML = `
        <div style="text-align:center;padding:2rem 0;">
          <svg width="38" height="38" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" style="opacity:.5">
            <g fill="none" fill-rule="evenodd"><g transform="translate(1 1)" stroke-width="2">
            <circle stroke-opacity=".25" cx="18" cy="18" r="18"/>
            <path d="M36 18c0-9.94-8.06-18-18-18"><animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="0.8s" repeatCount="indefinite"/></path>
            </g></g>
          </svg>
        </div>`;
      this.spinnerEl.style.display = 'none';
      this.productGrid.parentNode.insertBefore(
        this.spinnerEl,
        this.productGrid.nextSibling
      );
    }

    /** Bind scroll listener with debounce */
    _bindScroll() {
      let timer = null;
      const handler = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => this._onScroll(), DEBOUNCE_MS);
      };
      window.addEventListener('scroll', handler, { passive: true });

      // Also use IntersectionObserver as a backup trigger
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) this._loadNextPage();
          },
          { rootMargin: `${SCROLL_THRESHOLD}px` }
        );
        this._observer.observe(this.spinnerEl);
      }
    }

    _onScroll() {
      if (this.loading || this.noMorePages) return;
      const scrollBottom =
        window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight - scrollBottom < SCROLL_THRESHOLD) {
        this._loadNextPage();
      }
    }

    async _loadNextPage() {
      if (this.loading || this.noMorePages || !this.nextUrl) return;
      this.loading = true;
      this.spinnerEl.style.display = 'block';

      try {
        // Use section rendering API for clean HTML
        const separator = this.nextUrl.includes('?') ? '&' : '?';
        const fetchUrl = `${this.nextUrl}${separator}section_id=${this.sectionId}`;

        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newGrid = doc.getElementById('product-grid');

        if (!newGrid) {
          this.noMorePages = true;
          return;
        }

        // Append new product cards to existing grid
        const newItems = newGrid.querySelectorAll('.grid__item');
        if (newItems.length === 0) {
          this.noMorePages = true;
          return;
        }

        // Append to the <ul> grid if it exists, otherwise fallback to the outer div
        const targetList = this.productGrid.querySelector('ul.product-grid') || this.productGrid;
        newItems.forEach((item) => {
          // Reset animation order for smooth entrance
          item.style.setProperty(
            '--animation-order',
            targetList.children.length
          );
          targetList.appendChild(item);
        });

        // Trigger scroll animations if Dawn uses them
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.productGrid);
        }

        // Re-initialize quick-add buttons for new products
        if (window.Shopify && window.Shopify.PaymentButton) {
          window.Shopify.PaymentButton.init();
        }

        // Find next page URL from the fetched page
        const newNextLink = doc.querySelector(
          '.pagination__item--prev'
        );
        if (newNextLink) {
          this.nextUrl = newNextLink.getAttribute('href');
        } else {
          this.nextUrl = null;
          this.noMorePages = true;
        }

        // Update product count display
        const newCount = doc.getElementById('ProductCount');
        if (newCount) {
          const countEl = document.getElementById('ProductCount');
          if (countEl) countEl.innerHTML = newCount.innerHTML;
        }
      } catch (err) {
        console.error('[InfiniteScroll] Error loading page:', err);
        // On error, show pagination as fallback
        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = '';
        }
        this.noMorePages = true;
      } finally {
        this.loading = false;
        if (this.noMorePages) {
          this.spinnerEl.style.display = 'none';
          if (this._observer) this._observer.disconnect();
        } else {
          this.spinnerEl.style.display = 'none';
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new InfiniteScroll());
  } else {
    new InfiniteScroll();
  }

  // Re-initialize after facet filter changes (Dawn replaces the grid container)
  const originalRender = window.FacetFiltersForm?.renderProductGridContainer;
  if (originalRender) {
    FacetFiltersForm.renderProductGridContainer = function (html) {
      originalRender(html);
      // Small delay to let the DOM update
      setTimeout(() => new InfiniteScroll(), 100);
    };
  } else {
    // Alternative: watch for DOM changes on the grid container
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      const observer = new MutationObserver(() => {
        // Debounce re-initialization
        clearTimeout(observer._timer);
        observer._timer = setTimeout(() => new InfiniteScroll(), 200);
      });
      observer.observe(container, { childList: true });
    }
  }
})();
